
import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { SendHorizontal } from 'lucide-react';

interface AIChatInputProps {
  onProcessed: (result: any) => void;
}

export const AIChatInput: React.FC<AIChatInputProps> = ({ onProcessed }) => {
  const [message, setMessage] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [conversation, setConversation] = useState<{role: string, content: string}[]>([
    {
      role: 'assistant',
      content: 'Hi there! I can help you add your medications. Please tell me about them.'
    }
  ]);

  const handleSend = () => {
    if (!message.trim()) return;
    
    // Add user message to conversation
    const updatedConversation = [
      ...conversation,
      { role: 'user', content: message }
    ];
    setConversation(updatedConversation);
    setIsProcessing(true);
    
    // Simulate AI processing
    setTimeout(() => {
      // Add AI response
      setConversation([
        ...updatedConversation,
        { 
          role: 'assistant', 
          content: 'I\'ve processed your medication. Let me add 10mg Lisinopril taken once daily to your list.'
        }
      ]);
      setIsProcessing(false);
      setMessage('');
      
      // Simulate successful medication addition
      setTimeout(() => {
        onProcessed({
          name: 'Lisinopril',
          dosage: '10mg',
          frequency: 'once daily'
        });
      }, 1000);
    }, 2000);
  };

  return (
    <div className="p-4 space-y-4 w-full max-w-md mx-auto">
      <p className="text-sm text-gray-600 text-center">
        Let our AI help you add medications through conversational chat.
      </p>
      
      <div className="border rounded-lg h-60 overflow-y-auto p-3 bg-gray-50">
        {conversation.map((item, index) => (
          <div 
            key={index} 
            className={`mb-2 p-2 rounded-lg max-w-[80%] ${
              item.role === 'user' 
                ? 'ml-auto bg-purple-100 text-purple-900' 
                : 'bg-gray-200 text-gray-800'
            }`}
          >
            {item.content}
          </div>
        ))}
        {isProcessing && (
          <div className="bg-gray-200 text-gray-800 p-2 rounded-lg max-w-[80%] animate-pulse">
            Thinking...
          </div>
        )}
      </div>
      
      <div className="flex gap-2">
        <textarea 
          className="w-full p-3 border rounded-md text-sm resize-none" 
          placeholder="Ask the AI: 'Can you help me add my blood pressure medication? I take 10mg Lisinopril every morning.'"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={3}
        />
        <Button 
          className="self-end"
          onClick={handleSend}
          disabled={!message.trim() || isProcessing}
        >
          <SendHorizontal className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};
